import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Part } from '../interface/part';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { PartUtilities } from '../class/part-utilities';
import { ApiPaginatedPartQueryParams, ApiPaginatedQueryParams, ApiPaginatedResponse, PaginationService } from './pagination.service';
import { Observable, firstValueFrom, of } from 'rxjs';
import { DataService } from './data.service';
import { API_ERRORS } from '../shared/lists/errors';
import { LOG_REPORTER_KEYS, LogReporterService } from './log-reporter.service';
import { ObjectId } from '../shared/type-aliases/object-id';
import { EnumDisableOrActiveStr } from '../interface/disableOrActive';

/**
 * Represents stock/cost/price data for a part in a specific branch
 */
export interface BranchPartData {
  companyId: string;
  companyName: string;
  cnpj: string;
  isMatrix: boolean;
  stock: number;
  cost: number;
  averageCost: number;
  saleValue: number;
  location: string;
}

/**
 * Represents availability data for a part in a specific unit (for transfer REQUEST flow)
 */
export interface PartAvailabilityItem {
  companyId: string;
  companyName: string;
  cnpj: string;
  isMatrix: boolean;
  availableStock: number;
  osStock: number;
  orderStock: number;
}

/**
 * Response structure for part availability endpoint
 */
export interface PartAvailabilityResponse {
  partId: string;
  partDescription: string;
  partCode: string;
  availability: PartAvailabilityItem[];
}


@Injectable({
  providedIn: 'root'
})
export class PartService {
  private _isFromMatrix = false;
  parts: Part[] = [];

  constructor(
    private http: HttpClient,
    private _dataService: DataService,
    private _logReporter: LogReporterService
  ) {
  }

  async get(id: string, cnpj?: string): Promise<Part> {
    const url = `${environment.mkgoURL}/api/v1/parts/${id}`;
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj || this._isFromMatrix));
    const response: any = await this.http
      .get(url, header)
      .pipe(first())
      .toPromise();
    if (!response.parts.length) {
      throw new Error(`Part with id: ${id} has not found.`);
    }
    const apiPart = response.parts[0];
    const part = PartUtilities.complyApp(apiPart);
    return part;
  }

  async getByIds(ids: string[]): Promise<Part[]> {
    if (!ids || ids.length === 0) {
      return [];
    }
    const url = `${environment.mkgoURL}/api/v1/parts/by-ids`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: any = await firstValueFrom(this.http.post(url, { ids }, header));
    return (response["parts"] as any[]).map(PartUtilities.complyApp);
  }

  /** @deprecated prefer to get a paginated list */
  async getAll(): Promise<Part[]> {
    const url = `${environment.mkgoURL}/api/v1/parts`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: any = await this.http
      .get(url, header)
      .pipe(first())
      .toPromise();
    (response["parts"] as Part[]).map(PartUtilities.complyApp);
    this.parts = (response["parts"] as Part[]).reverse();
    return this.parts;
  }

  async getAllByCondition(condition: 1 | 2 | 3): Promise<Part[]> {
    const url = `${environment.mkgoURL}/api/v1/parts/find/with/conditions?stockCondition=${condition}`
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(this.http.get(url, header).pipe(first()));
    let parts: Part[];
    if (Array.isArray(response)) {
      parts = response.map(part => PartUtilities.complyApp(part));
    }
    return parts;
  }


  getInactiveParts(): Observable<{ parts: Part[] }> {
    const url = `${environment.mkgoURL}/api/v1/parts/inactives`;
    return this._dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(options =>
        this.http.get<{ parts: Part[] }>(url, options).pipe(
          first(),
          catchError((err) => {
            console.error(err)
            return of({ parts: [] })
          })
        ),
      ),
    )
  }

  getPaginated(apiParams: ApiPaginatedPartQueryParams, cnpj?: string): Observable<ApiPaginatedResponse<Part>> {
    const url = `${environment.mkgoURL}/api/v1/parts/pages`;
    let params = PaginationService.getParams(apiParams);
    return this._dataService.httpOptions(cnpj || this._isFromMatrix, params).pipe(
      switchMap(options => this.http.get<ApiPaginatedResponse<Part>>(url, options)),
      first(),
      catchError(err => {
        if (err.error.error === API_ERRORS.notFound) {
          return of(this._notFoundErrorDataToReturn())
        }
      }),
      switchMap(resp => {
        // the API return 100 docs by defult (even if all=1), so if we want all docs we must call it again with param `limit`
        if (apiParams.all && resp.hasNextPage) {
          apiParams.limit = resp.totalDocs;
          params = PaginationService.getParams(apiParams);
          return this._dataService.httpOptions(this._isFromMatrix, params).pipe(
            switchMap(options2 => this.http.get<ApiPaginatedResponse<Part>>(url, options2)),
            catchError(err => {
              if (err.error.error === API_ERRORS.notFound) {
                return of(this._notFoundErrorDataToReturn())
              } else {
                throw err;
              }
            }),
            first(),
          )
        } else {
          return of(resp)
        }
      }),
    );
  }

  private _notFoundErrorDataToReturn() {
    return {
      docs: [],
      hasNextPage: false,
      hasPrevPage: false,
      limit: 0,
      nextPage: null,
      page: 0,
      pagingCounter: 0,
      prevPage: null,
      totalDocs: 0,
      totalPages: 0
    }
  }

  async addOrUpdate(part: Part, caller: string, updatePartStockFields: boolean): Promise<string> {
    const errLogger = this._logReporter.errorLogger(`${caller} => PartService.addOrUpdate()`, LOG_REPORTER_KEYS.STOCK);
    try {
      const apipart = PartUtilities.complyAPI(part, updatePartStockFields);
      const url = `${environment.mkgoURL}/api/v1/parts`;
      const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
      const { id } = await firstValueFrom(this.http.post<{ id: ObjectId }>(url, apipart, header));
      return id;
    } catch (error) {
      errLogger(error)
      throw error
    }
  }

  async addOrUpdateMany(parts: Part[], updatePartStockFields: boolean) {
    let part;
    let promises: Promise<any>[] = [];
    for (let i = 0; i < parts.length; i++) {
      part = parts[i];
      promises.push(this.addOrUpdate(part, "PartService.addOrUpdateMany()", updatePartStockFields));
    }
    await Promise.all(promises);
  }

  /*async delete(id: string): Promise<Part> {
    const url = `${environment.mkgoURL}/api/v1/parts/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this.http
      .delete(url, header)
      .pipe(first())
      .toPromise();
    return resp;
  }*/
  async delete(id: string, action: EnumDisableOrActiveStr): Promise<Part> {
    const url = `${environment.mkgoURL}/api/v1/parts/${id}/${action}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this.http
      .put(url, {}, header)
      .pipe(first())
      .toPromise();
    return resp;
  }

  async checkUse(id: string) {
    const url = `${environment.mkgoURL}/api/v1/parts/${id}/check/in/use`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(this.http.get<{ inUse: boolean }>(url, header))
    return response?.inUse;
  }

  findByFragaId(idFraga: string): Observable<Part | undefined> {
    if (!idFraga) {
      return;
    }
    const url = `${environment.mkgoURL}/api/v1/parts/id-fraga/${idFraga}`;
    return this._dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(options => this.http.get<{ parts: Part[] }>(url, options)),
      map(resp => resp.parts),
      map(arr => !!arr.length ? arr[0] : undefined)
    )
  }

  /**
   * Get branch data (stock, cost, price) for a specific part across all branches
   * @param partId Part ID
   * @returns Observable with array of branch part data
   */
  getBranchDataByPart(partId: string): Observable<BranchPartData[]> {
    if (!partId) {
      return of([]);
    }
    const url = `${environment.mkgoURL}/api/v1/branch-parts/by-part/${partId}`;
    return this._dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(options => this.http.get<{ branchParts: BranchPartData[] }>(url, options)),
      map(resp => resp.branchParts || []),
      first(),
      catchError(err => {
        console.error('Error getting branch data for part:', err);
        return of([]);
      })
    );
  }

  /**
   * Get part availability across all units (for transfer REQUEST flow)
   * Returns only units with available stock > 0, excluding the current company
   * @param partId Part ID
   * @returns Observable with availability data
   */
  getPartAvailability(partId: string): Observable<PartAvailabilityResponse> {
    if (!partId) {
      return of({ partId: '', partDescription: '', partCode: '', availability: [] });
    }
    const url = `${environment.mkgoURL}/api/v1/branch-parts/availability/${partId}`;
    // Use false to query with current company's CNPJ (not matrix)
    return this._dataService.httpOptions(false).pipe(
      switchMap(options => this.http.get<PartAvailabilityResponse>(url, options)),
      first(),
      catchError(err => {
        console.error('Error getting part availability:', err);
        return of({ partId, partDescription: '', partCode: '', availability: [] });
      })
    );
  }

  /**

   * Adjust stock for a branch part (used for multi-branch companies)
   * Calls PUT /branch-parts/:partId/stock
   * 
   * @param partId Part ID
   * @param quantity Amount to add or subtract
   * @param operation 'add' to increase stock, 'subtract' to decrease stock
   * @param moveType Type of stock movement (affects which field: stock, osStock, orderStock)
   * @returns Promise with the updated branch part data
   */
  async adjustBranchPartStock(
    partId: string, 
    quantity: number, 
    operation: 'add' | 'subtract',
    moveType?: 'stock' | 'osStock' | 'orderStock'
  ): Promise<any> {
    const errLogger = this._logReporter.errorLogger(
      `PartService.adjustBranchPartStock()`, 
      LOG_REPORTER_KEYS.STOCK
    );
    
    try {
      const url = `${environment.mkgoURL}/api/v1/branch-parts/${partId}/stock`;
      // Note: Use false for fromMatrix to ensure we're updating the current company's BranchPart
      const header = await firstValueFrom(this._dataService.httpOptions(false));
      const body = { 
        quantity, 
        operation,
        moveType: moveType || 'stock'
      };
      return firstValueFrom(this.http.put(url, body, header));
    } catch (error) {
      errLogger(error);
      throw error;
    }
  }

  /**
   * Get images for a specific part
   * @param partId Part ID
   * @returns Observable with array of images
   */
  getPartImages(partId: string): Observable<{ base64: string; order: number; createdAt: Date }[]> {
    const url = `${environment.mkgoURL}/api/v1/part-images/${partId}`;
    return this._dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(options => this.http.get<{ images: any[] }>(url, options)),
      map(resp => resp.images || []),
      first(),
      catchError(err => {
        console.error('Error getting part images:', err);
        return of([]);
      })
    );
  }

  /**
   * Get image count for a part (lightweight)
   * @param partId Part ID
   * @returns Observable with count
   */
  getPartImageCount(partId: string): Observable<number> {
    const url = `${environment.mkgoURL}/api/v1/part-images/${partId}/count`;
    return this._dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(options => this.http.get<{ count: number }>(url, options)),
      map(resp => resp.count || 0),
      first(),
      catchError(err => {
        console.error('Error getting part image count:', err);
        return of(0);
      })
    );
  }

  /**
   * Save images for a part (create or update)
   * @param partId Part ID
   * @param images Array of images in base64
   * @returns Promise with response
   */
  async savePartImages(partId: string, images: { base64: string; order: number }[]): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/part-images/${partId}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    return firstValueFrom(this.http.post(url, { images }, header));
  }

  /**
   * Delete a specific image from a part
   * @param partId Part ID
   * @param imageIndex Image index/order
   * @returns Promise with response
   */
  async deletePartImage(partId: string, imageIndex: number): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/part-images/${partId}/${imageIndex}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    return firstValueFrom(this.http.delete(url, header));
  }
}
